import { supabase } from '../../config/supabase';

/**
 * Fetch dashboard statistics for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Dashboard stats + recent jobs + status breakdown
 */
export const getDashboardStats = async (companyId) => {
    try {
        const { data: jobs, error } = await supabase
            .from('jobs')
            .select(`
                id, unique_id, customer_name, job_type, main_status,
                created_at, urgent_design, urgent_quote,
                job_quotes(id, quote_type, is_required, design_status,
                           estimation_status, delivery_status, production_status,
                           child_unique_id)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false });

        if (error) throw error;

        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);

        const activeJobs = jobs?.filter(j => j.main_status !== 'completed') || [];

        const stats = {
            activeJobs:    activeJobs.length,
            completedJobs: jobs?.filter(j => j.main_status === 'completed').length ?? 0,
            pendingJobs:   jobs?.filter(j =>
                ['pending_design', 'pending_estimation', 'ready_for_delivery'].includes(j.main_status)
            ).length ?? 0,
            urgentJobs:    jobs?.filter(j => j.urgent_design || j.urgent_quote).length ?? 0,
            thisWeekJobs:  jobs?.filter(j => new Date(j.created_at) >= weekAgo).length ?? 0,
        };

        const statusBreakdown = [
            { key: 'pending_design',     label: 'Pending Design',     count: activeJobs.filter(j => j.main_status === 'pending_design').length },
            { key: 'pending_estimation', label: 'Pending Estimate',   count: activeJobs.filter(j => j.main_status === 'pending_estimation').length },
            { key: 'in_progress',        label: 'In Progress',        count: activeJobs.filter(j => j.main_status === 'in_progress').length },
            { key: 'ready_for_delivery', label: 'Ready for Delivery', count: activeJobs.filter(j => j.main_status === 'ready_for_delivery').length },
        ];

        const recentJobs = jobs?.slice(0, 10) || [];

        const { count: totalUsers, error: usersError } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId);

        if (usersError) throw usersError;

        return {
            ...stats,
            totalUsers:      totalUsers ?? 0,
            statusBreakdown,
            recentJobs,
        };
    } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        throw error;
    }
};
